import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  FaArrowLeft,
  FaAward,
  FaCheck,
  FaCheckCircle,
  FaCopy,
  FaDesktop,
  FaDiscord,
  FaEnvelope,
  FaExclamationCircle,
  FaEye,
  FaEyeSlash,
  FaGlobe,
  FaLock,
  FaMobileAlt,
  FaClipboardList,
  FaSearch,
  FaShieldAlt,
  FaSignOutAlt,
  FaSpinner,
  FaTools,
  FaTrashAlt,
  FaUser,
  FaUsers,
  FaWallet,
} from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { authService, contactService, userService } from '../../services/apiService';
import VihsMark from '../../components/VihsMark';
import AvatarEditor, { AvatarEditTrigger } from '../../components/AvatarEditor';
import {
  AUTH_POLICY,
  evaluatePasswordClient,
  evaluateUsernameClient,
} from '../../utils/authPolicyClient';
import './Account.css';

const SECTIONS = [
  { id: 'perfil', label: 'Perfil', icon: FaUser },
  { id: 'pedidos', label: 'Pedidos', icon: FaClipboardList },
  { id: 'seguranca', label: 'Segurança', icon: FaShieldAlt },
  { id: 'sessoes', label: 'Sessões', icon: FaDesktop },
  { id: 'conexoes', label: 'Conexões', icon: FaDiscord },
  { id: 'risco', label: 'Zona de risco', icon: FaTrashAlt },
];

const REQUEST_STATUS_TONE = {
  pending: 'warn',
  in_review: 'info',
  awaiting_client: 'info',
  quoted: 'info',
  accepted: 'ok',
  in_progress: 'ok',
  completed: 'ok',
  declined: 'danger',
  cancelled: 'danger',
};

function formatDate(value, options = { dateStyle: 'short', timeStyle: 'short' }) {
  if (!value) return '—';
  try {
    return new Intl.DateTimeFormat('pt-BR', options).format(new Date(value));
  } catch {
    return '—';
  }
}

function getInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return String(name || '?').slice(0, 2).toUpperCase();
}

function parseUserAgent(ua) {
  const source = ua || '';
  let browser = 'Navegador';
  let os = 'Sistema';
  let device = 'desktop';

  if (/Edg\//.test(source)) browser = 'Edge';
  else if (/OPR\/|Opera/.test(source)) browser = 'Opera';
  else if (/Chrome\//.test(source)) browser = 'Chrome';
  else if (/Firefox\//.test(source)) browser = 'Firefox';
  else if (/Safari\//.test(source)) browser = 'Safari';

  if (/Windows NT/.test(source)) os = 'Windows';
  else if (/Mac OS X/.test(source)) os = 'macOS';
  else if (/Android/.test(source)) {
    os = 'Android';
    device = 'mobile';
  } else if (/iPhone|iPad|iPod/.test(source)) {
    os = 'iOS';
    device = 'mobile';
  } else if (/Linux/.test(source)) os = 'Linux';

  return { browser, os, device, raw: source };
}

function formatIp(ip) {
  if (!ip || ip === '::1' || ip === '127.0.0.1') return 'Este computador';
  return ip;
}

function Field({ icon: Icon, label, hint, action, textarea, children, ...inputProps }) {
  return (
    <div className="account-field">
      <div className="account-label-row">
        <label htmlFor={inputProps.id}>{label}</label>
        {action}
      </div>
      <div className={`account-input-wrap${textarea ? ' is-textarea' : ''}${inputProps.disabled ? ' is-locked' : ''}`}>
        {Icon ? <Icon aria-hidden="true" /> : null}
        {textarea ? <textarea {...inputProps} /> : <input {...inputProps} />}
        {children}
      </div>
      {hint ? <p className="account-field-hint">{hint}</p> : null}
    </div>
  );
}

export default function Account() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, updateUser, logout, logoutAll, refreshUser } = useAuth();
  const [profileForm, setProfileForm] = useState({
    username: user?.username || '',
    displayName: user?.profile?.displayName || '',
    bio: user?.profile?.bio || '',
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    password: '',
    passwordConfirm: '',
  });
  const [sessions, setSessions] = useState([]);
  const [requests, setRequests] = useState([]);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [status, setStatus] = useState(null);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(null);
  const [activeSection, setActiveSection] = useState('perfil');
  const [confirmDanger, setConfirmDanger] = useState(null);
  const [deactivateInput, setDeactivateInput] = useState('');
  const [suggested, setSuggested] = useState([]);
  const [loadingSuggested, setLoadingSuggested] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [linkingDiscord, setLinkingDiscord] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);

  const displayName = user?.profile?.displayName || user?.username || 'sua conta';
  const hasLocalProvider = user?.providers?.some((provider) => provider.type === 'local') || !user?.discordId;
  const hasDiscord = Boolean(user?.discordId) || user?.providers?.some((provider) => provider.type === 'discord');
  const passwordEval = useMemo(
    () => evaluatePasswordClient(passwordForm.password, {
      username: user?.username,
      email: user?.email,
    }),
    [passwordForm.password, user?.username, user?.email]
  );
  const profileDirty = useMemo(() => (
    profileForm.username !== (user?.username || '')
    || profileForm.displayName !== (user?.profile?.displayName || '')
    || profileForm.bio !== (user?.profile?.bio || '')
  ), [profileForm, user]);
  const usernameValid = useMemo(() => {
    const value = profileForm.username.trim();
    const current = String(user?.username || '').trim();
    if (value.toLowerCase() === current.toLowerCase()) {
      return value.length >= 3
        && value.length <= AUTH_POLICY.USERNAME_MAX
        && /^[a-zA-Z0-9._-]+$/.test(value);
    }
    return evaluateUsernameClient(value).ok;
  }, [profileForm.username, user?.username]);
  const passwordMismatch = passwordForm.passwordConfirm.length > 0
    && passwordForm.password !== passwordForm.passwordConfirm;

  useEffect(() => {
    document.title = "Conta — Vih's";
    return () => {
      document.title = "Vih's — criação digital sob medida";
    };
  }, []);

  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) return;

    if (hash === 'account-pedidos' || hash === 'pedidos') {
      window.setTimeout(() => {
        const target = document.getElementById('account-pedidos');
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setActiveSection('pedidos');
        }
      }, 120);
      return;
    }

    const params = new URLSearchParams(hash);
    const discordResult = params.get('discord');
    if (!discordResult) return;

    window.history.replaceState(null, '', window.location.pathname);

    if (discordResult === 'linked') {
      setStatus({ type: 'success', message: 'Discord vinculado à sua conta com sucesso.' });
      refreshUser().catch(() => {});
      return;
    }

    if (discordResult === 'error') {
      const rawMessage = params.get('message');
      setStatus({
        type: 'error',
        message: rawMessage
          ? decodeURIComponent(rawMessage)
          : 'Não foi possível vincular o Discord.',
      });
    }
  }, [refreshUser]);

  useEffect(() => {
    setProfileForm({
      username: user?.username || '',
      displayName: user?.profile?.displayName || '',
      bio: user?.profile?.bio || '',
    });
  }, [user]);

  const loadSessions = useCallback(async () => {
    setLoadingSessions(true);
    try {
      const response = await authService.listSessions();
      setSessions(response?.data?.sessions || []);
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  const loadRequests = useCallback(async () => {
    setLoadingRequests(true);
    try {
      const response = await contactService.listMyRequests();
      const next = response?.data?.requests || [];
      setRequests(next);
      setSelectedRequestId((current) => {
        if (current && next.some((item) => item.id === current)) return current;
        return next[0]?.id || null;
      });
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setLoadingRequests(false);
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoadingSuggested(true);
      try {
        const res = await userService.getSuggestedProfiles(5);
        if (active) setSuggested(res.data.profiles || []);
      } catch {
        if (active) setSuggested([]);
      } finally {
        if (active) setLoadingSuggested(false);
      }
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 1) {
      setSearchResults([]);
      setSearching(false);
      return undefined;
    }

    let active = true;
    const timer = window.setTimeout(async () => {
      setSearching(true);
      try {
        const res = await userService.searchUsers(q, 8);
        if (active) setSearchResults(res.data.users || []);
      } catch {
        if (active) setSearchResults([]);
      } finally {
        if (active) setSearching(false);
      }
    }, 280);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [searchQuery]);

  useEffect(() => {
    const nodes = SECTIONS
      .map((section) => document.getElementById(`account-${section.id}`))
      .filter(Boolean);

    if (!nodes.length) return undefined;

    const observer = new IntersectionObserver((entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (visible) {
        setActiveSection(visible.target.id.replace('account-', ''));
      }
    }, { rootMargin: '-18% 0px -62% 0px', threshold: [0.15, 0.4] });

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  const flashStatus = (next) => {
    setStatus(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const copyValue = async (key, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      window.setTimeout(() => setCopied(null), 1600);
    } catch {
      flashStatus({ type: 'error', message: 'Não foi possível copiar.' });
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(`account-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSection(id);
  };

  const handleAvatarSave = async (file) => {
    setAvatarBusy(true);
    setStatus(null);
    try {
      const response = await userService.updateAvatar(file);
      updateUser(response?.data?.user || null);
      setAvatarOpen(false);
      flashStatus({ type: 'success', message: t('avatar.saved') });
    } catch (error) {
      flashStatus({ type: 'error', message: error.message || t('avatar.saveFailed') });
    } finally {
      setAvatarBusy(false);
    }
  };

  const handleAvatarRemove = async () => {
    setAvatarBusy(true);
    setStatus(null);
    try {
      const response = await userService.deleteAvatar();
      updateUser(response?.data?.user || null);
      setAvatarOpen(false);
      flashStatus({ type: 'success', message: t('avatar.removed') });
    } catch (error) {
      flashStatus({ type: 'error', message: error.message || t('avatar.removeFailed') });
    } finally {
      setAvatarBusy(false);
    }
  };

  const handleProfileSave = async (event) => {
    event.preventDefault();
    if (!profileDirty) return;
    if (!usernameValid) {
      flashStatus({
        type: 'error',
        message: `O nome de usuário deve ter ${AUTH_POLICY.USERNAME_MIN}–${AUTH_POLICY.USERNAME_MAX} caracteres e começar com letra.`,
      });
      return;
    }
    setSavingProfile(true);
    setStatus(null);
    const previousUsername = user?.username || '';
    try {
      const response = await userService.updateMe({
        username: profileForm.username.trim(),
        profile: {
          displayName: profileForm.displayName,
          bio: profileForm.bio,
        },
      });
      const nextUser = response?.data?.user || null;
      updateUser(nextUser);
      const usernameChanged = nextUser?.username && nextUser.username !== previousUsername;
      flashStatus({
        type: 'success',
        message: usernameChanged
          ? `Perfil atualizado. Seu perfil público agora é /perfil/${nextUser.username}`
          : 'Perfil atualizado com sucesso.',
      });
    } catch (error) {
      flashStatus({ type: 'error', message: error.message });
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSave = async (event) => {
    event.preventDefault();
    if (!passwordEval.ok) {
      flashStatus({ type: 'error', message: 'Crie uma senha que atenda a todos os requisitos de segurança.' });
      return;
    }
    if (passwordMismatch) {
      flashStatus({ type: 'error', message: 'As senhas novas não coincidem.' });
      return;
    }

    setSavingPassword(true);
    setStatus(null);
    try {
      const response = await authService.updatePassword(passwordForm);
      updateUser(response?.data?.user || null);
      setPasswordForm({ currentPassword: '', password: '', passwordConfirm: '' });
      flashStatus({ type: 'success', message: 'Senha atualizada. As outras sessões foram encerradas.' });
      await loadSessions();
    } catch (error) {
      flashStatus({ type: 'error', message: error.message });
    } finally {
      setSavingPassword(false);
    }
  };

  const handleRevokeSession = async (sessionId) => {
    try {
      await authService.revokeSession(sessionId);
      setSessions((current) => current.filter((item) => item.id !== sessionId));
      flashStatus({ type: 'success', message: 'Sessão encerrada neste dispositivo.' });
    } catch (error) {
      flashStatus({ type: 'error', message: error.message });
    }
  };

  const handleDiscordLink = async () => {
    setLinkingDiscord(true);
    try {
      const url = await authService.getDiscordLinkAuthorizeUrl();
      window.location.href = url;
    } catch (error) {
      flashStatus({ type: 'error', message: error.message });
      setLinkingDiscord(false);
    }
  };

  const handleDeactivate = async () => {
    if (deactivateInput.trim() !== user?.username) return;
    setDeactivating(true);
    try {
      await userService.deactivateMe();
      authService.clearSession();
      updateUser(null);
      navigate('/', { replace: true });
    } catch (error) {
      flashStatus({ type: 'error', message: error.message });
      setDeactivating(false);
    }
  };

  return (
    <main className="account-page">
      <div className="account-atmosphere" aria-hidden="true">
        <div className="account-orb account-orb-one" />
        <div className="account-orb account-orb-two" />
        <div className="account-grid-bg" />
      </div>

      <header className="account-topbar">
        <Link to="/" className="account-brand" aria-label="Vih's — página inicial">
          <VihsMark />
          Vih&apos;s
        </Link>
        <Link to="/" className="account-back">
          <FaArrowLeft aria-hidden="true" />
          Voltar ao início
        </Link>
      </header>

      <div className="account-shell">
        <motion.section
          className="account-identity"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="account-identity-main">
            <AvatarEditTrigger
              currentUrl={user?.avatarUrl}
              displayName={displayName}
              frameClassName="account-avatar"
              onClick={() => setAvatarOpen(true)}
            />
            <div>
              <p className="account-eyebrow">Espaço pessoal</p>
              <h1>{displayName}</h1>
              {user?.verified && <span className="account-verified" title="Conta verificada"><FaCheckCircle aria-hidden="true" /> Verificado</span>}
              <p className="account-identity-meta">
                @{user?.username}
                <span aria-hidden="true">·</span>
                {user?.email}
              </p>
              <div className="account-badges">
                <span className={`account-badge ${user?.emailVerified ? 'ok' : 'warn'}`}>
                  {user?.emailVerified ? 'E-mail verificado' : 'E-mail pendente'}
                </span>
                <span className="account-badge">
                  {user?.role === 'admin' ? 'Administrador' : 'Membro'}
                </span>
                {(user?.badges || []).map((badge) => (
                  <span
                    key={badge.id}
                    className="account-badge account-custom-badge"
                    style={{ color: badge.color, backgroundColor: badge.backgroundColor, borderColor: badge.color }}
                    title={badge.description || badge.name}
                  >
                    <FaAward aria-hidden="true" /> {badge.name}
                  </span>
                ))}
                {hasDiscord && <span className="account-badge discord">Discord vinculado</span>}
              </div>
            </div>
          </div>

          <dl className="account-stats">
            <div>
              <dt>Membro desde</dt>
              <dd>{formatDate(user?.createdAt, { dateStyle: 'long' })}</dd>
            </div>
            <div>
              <dt>Último acesso</dt>
              <dd>{formatDate(user?.lastLoginAt)}</dd>
            </div>
            <div>
              <dt>Sessões ativas</dt>
              <dd>{loadingSessions ? '—' : sessions.length}</dd>
            </div>
          </dl>
        </motion.section>

        {status && (
          <div className={`account-alert ${status.type}`} role="status">
            {status.type === 'success' ? <FaCheckCircle /> : <FaExclamationCircle />}
            <span>{status.message}</span>
          </div>
        )}

        <div className="account-layout">
          <nav className="account-nav" aria-label="Seções da conta">
            {SECTIONS.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  type="button"
                  className={activeSection === section.id ? 'is-active' : ''}
                  onClick={() => scrollToSection(section.id)}
                >
                  <Icon aria-hidden="true" />
                  {section.label}
                </button>
              );
            })}
            {user?.role === 'admin' && (
              <Link to="/admin" className="account-nav-link account-nav-admin">
                <FaTools aria-hidden="true" />
                Painel admin
              </Link>
            )}
            {user?.canAccessFinance && (
              <Link to="/financas" className="account-nav-link account-nav-admin">
                <FaWallet aria-hidden="true" />
                Finanças
              </Link>
            )}
            <Link to="/feed" className="account-nav-link">
              <FaGlobe aria-hidden="true" />
              Feed
            </Link>
            <Link to="/explorar" className="account-nav-link">
              <FaUsers aria-hidden="true" />
              Explorar
            </Link>
          </nav>

          <div className="account-panels">
            <section id="account-perfil" className="account-card">
              <div className="account-card-head">
                <div>
                  <span className="account-card-eyebrow">Identidade</span>
                  <h2>Perfil público</h2>
                  <p>Como você aparece no ecossistema Vih&apos;s. O e-mail permanece bloqueado.</p>
                </div>
                <Link to={`/perfil/${user?.username || ''}`} className="account-btn account-btn-ghost account-view-public">
                  <FaEye aria-hidden="true" />
                  Ver perfil público
                </Link>
              </div>

              <form onSubmit={handleProfileSave} className="account-form">
                <div className="account-form-grid">
                  <Field
                    icon={FaUser}
                    label="Nome de usuário"
                    id="username"
                    value={profileForm.username}
                    onChange={(e) => setProfileForm((current) => ({
                      ...current,
                      username: e.target.value.replace(/\s/g, '').slice(0, 20),
                    }))}
                    maxLength={20}
                    autoComplete="username"
                    placeholder="seu_usuario"
                    hint="3–20 caracteres: letras, números, ponto, hífen ou underscore. Altera a URL do perfil."
                    action={(
                      <button
                        type="button"
                        className="account-inline"
                        onClick={() => copyValue('username', profileForm.username || '')}
                      >
                        {copied === 'username' ? <FaCheck /> : <FaCopy />}
                        {copied === 'username' ? 'Copiado' : 'Copiar'}
                      </button>
                    )}
                  />
                  <Field
                    icon={FaEnvelope}
                    label="E-mail"
                    id="email"
                    value={user?.email || ''}
                    disabled
                    action={(
                      <button type="button" className="account-inline" onClick={() => copyValue('email', user?.email || '')}>
                        {copied === 'email' ? <FaCheck /> : <FaCopy />}
                        {copied === 'email' ? 'Copiado' : 'Copiar'}
                      </button>
                    )}
                  />
                </div>

                <Field
                  icon={FaUser}
                  label="Nome de exibição"
                  id="displayName"
                  value={profileForm.displayName}
                  onChange={(e) => setProfileForm((current) => ({ ...current, displayName: e.target.value }))}
                  maxLength={40}
                  placeholder="Como você aparece"
                  action={<span className="account-counter">{profileForm.displayName.length}/40</span>}
                />

                <Field
                  textarea
                  label="Bio"
                  id="bio"
                  value={profileForm.bio}
                  onChange={(e) => setProfileForm((current) => ({ ...current, bio: e.target.value }))}
                  maxLength={280}
                  rows={4}
                  placeholder="Uma linha sobre você, seus projetos ou o que você constrói."
                  action={<span className="account-counter">{profileForm.bio.length}/280</span>}
                />

                <div className="account-card-footer">
                  <p>Alterações de perfil não afetam seu login.</p>
                  <button type="submit" className="account-btn" disabled={savingProfile || !profileDirty || !usernameValid}>
                    {savingProfile ? <FaSpinner className="account-spin" /> : null}
                    {savingProfile ? 'Salvando...' : 'Salvar perfil'}
                  </button>
                </div>
              </form>
            </section>

            <section id="account-pedidos" className="account-card">
              <div className="account-card-head">
                <div>
                  <span className="account-card-eyebrow">Studio</span>
                  <h2>Pedidos de projeto</h2>
                  <p>Acompanhe status, histórico e respostas da equipe Vih&apos;s.</p>
                </div>
                <div className="account-card-actions">
                  <span className="account-count">{loadingRequests ? '…' : requests.length}</span>
                  <button
                    type="button"
                    className="account-btn account-btn-ghost"
                    onClick={loadRequests}
                    disabled={loadingRequests}
                  >
                    {loadingRequests ? <FaSpinner className="account-spin" /> : null}
                    Atualizar
                  </button>
                </div>
              </div>

              {loadingRequests ? (
                <p className="account-muted">Carregando pedidos...</p>
              ) : requests.length === 0 ? (
                <div className="account-empty">
                  <FaClipboardList aria-hidden="true" />
                  <strong>Nenhum pedido ainda</strong>
                  <p>
                    Quando você solicitar um projeto no site com este e-mail, ele aparece aqui
                    com status em tempo real. Só é permitido 1 pedido ativo por vez.
                  </p>
                </div>
              ) : (
                <div className="account-requests">
                  <ul className="account-request-list">
                    {requests.map((request) => {
                      const tone = REQUEST_STATUS_TONE[request.status] || 'warn';
                      const active = selectedRequestId === request.id;
                      return (
                        <li key={request.id}>
                          <button
                            type="button"
                            className={`account-request-item${active ? ' is-active' : ''}`}
                            onClick={() => setSelectedRequestId(request.id)}
                          >
                            <div>
                              <strong>{request.code}</strong>
                              <p>{request.services?.slice(0, 2).join(' · ') || 'Projeto'}</p>
                            </div>
                            <span className={`account-badge ${tone}`}>{request.statusLabel}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>

                  {(() => {
                    const selected = requests.find((item) => item.id === selectedRequestId) || requests[0];
                    if (!selected) return null;
                    const tone = REQUEST_STATUS_TONE[selected.status] || 'warn';
                    return (
                      <article className="account-request-detail">
                        <header>
                          <div>
                            <p className="account-card-eyebrow">{selected.code}</p>
                            <h3>{selected.company || selected.name}</h3>
                            <p>
                              Aberto em {formatDate(selected.createdAt)}
                              {selected.respondedAt ? (
                                <>
                                  <span aria-hidden="true"> · </span>
                                  Respondido em {formatDate(selected.respondedAt)}
                                </>
                              ) : null}
                            </p>
                          </div>
                          <span className={`account-badge ${tone}`}>{selected.statusLabel}</span>
                        </header>

                        {selected.clientMessage ? (
                          <div className="account-request-message">
                            <strong>Mensagem da equipe</strong>
                            <p>{selected.clientMessage}</p>
                          </div>
                        ) : null}

                        <dl className="account-request-meta">
                          <div>
                            <dt>Serviços</dt>
                            <dd>{selected.services?.join(', ') || '—'}</dd>
                          </div>
                          <div>
                            <dt>Orçamento</dt>
                            <dd>{selected.budget || '—'}</dd>
                          </div>
                          <div>
                            <dt>Prazo</dt>
                            <dd>{selected.timeline || '—'}</dd>
                          </div>
                          <div>
                            <dt>Última atualização</dt>
                            <dd>{formatDate(selected.lastStatusAt || selected.updatedAt)}</dd>
                          </div>
                        </dl>

                        <div className="account-request-block">
                          <strong>Briefing</strong>
                          <p>{selected.description}</p>
                        </div>

                        {selected.references ? (
                          <div className="account-request-block">
                            <strong>Referências</strong>
                            <p>{selected.references}</p>
                          </div>
                        ) : null}

                        <div className="account-request-timeline">
                          <strong>Histórico</strong>
                          <ol>
                            {(selected.history || []).slice().reverse().map((event, index) => (
                              <li key={`${event.status}-${event.changedAt}-${index}`}>
                                <span className={`account-badge ${REQUEST_STATUS_TONE[event.status] || 'warn'}`}>
                                  {event.label || event.status}
                                </span>
                                <div>
                                  <p>{event.note || 'Atualização de status.'}</p>
                                  <small>{formatDate(event.changedAt)}</small>
                                </div>
                              </li>
                            ))}
                          </ol>
                        </div>
                      </article>
                    );
                  })()}
                </div>
              )}
            </section>

            <section id="account-seguranca" className="account-card">
              <div className="account-card-head">
                <div>
                  <span className="account-card-eyebrow">Acesso</span>
                  <h2>Segurança</h2>
                  <p>Atualize sua senha com calma. As outras sessões serão encerradas depois da troca.</p>
                </div>
              </div>

              {hasLocalProvider ? (
                <form onSubmit={handlePasswordSave} className="account-form">
                  <Field
                    icon={FaLock}
                    label="Senha atual"
                    id="currentPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm((current) => ({ ...current, currentPassword: e.target.value }))}
                    autoComplete="current-password"
                    required
                  />
                  <Field
                    icon={FaLock}
                    label="Nova senha"
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={passwordForm.password}
                    onChange={(e) => setPasswordForm((current) => ({ ...current, password: e.target.value }))}
                    autoComplete="new-password"
                    minLength={AUTH_POLICY.PASSWORD_MIN}
                    required
                    hint={(
                      <div className="account-strength" aria-label={`Força da senha: ${passwordEval.label}`}>
                        <div>
                          {[1, 2, 3, 4, 5, 6, 7].map((level) => (
                            <span key={level} className={passwordEval.score >= level ? 'filled' : ''} />
                          ))}
                        </div>
                        <p>{passwordEval.label} · mínimo {AUTH_POLICY.PASSWORD_MIN} caracteres, letras, número e símbolo.</p>
                      </div>
                    )}
                  />
                  <Field
                    icon={FaLock}
                    label="Confirmar nova senha"
                    id="passwordConfirm"
                    type={showPassword ? 'text' : 'password'}
                    value={passwordForm.passwordConfirm}
                    onChange={(e) => setPasswordForm((current) => ({ ...current, passwordConfirm: e.target.value }))}
                    autoComplete="new-password"
                    minLength={8}
                    required
                    hint={passwordMismatch ? <p className="account-field-error">As senhas não coincidem.</p> : null}
                  />

                  <label className="account-show-password">
                    <input
                      type="checkbox"
                      checked={showPassword}
                      onChange={() => setShowPassword((current) => !current)}
                    />
                    <span>{showPassword ? <FaEyeSlash /> : <FaEye />}</span>
                    Mostrar senhas
                  </label>

                  <div className="account-card-footer">
                    <p>Uma senha forte protege projetos, conversas e sessões abertas.</p>
                    <button type="submit" className="account-btn" disabled={savingPassword}>
                      {savingPassword ? <FaSpinner className="account-spin" /> : null}
                      {savingPassword ? 'Atualizando...' : 'Atualizar senha'}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="account-empty">
                  <FaDiscord />
                  <div>
                    <strong>Esta conta entra pelo Discord</strong>
                    <p>Não há senha local para alterar. O acesso continua vinculado à sua conta Discord.</p>
                  </div>
                </div>
              )}
            </section>

            <section id="account-sessoes" className="account-card">
              <div className="account-card-head">
                <div>
                  <span className="account-card-eyebrow">Dispositivos</span>
                  <h2>Sessões ativas</h2>
                  <p>Veja onde sua conta está aberta e encerre o que não reconhecer.</p>
                </div>
                <span className="account-count">{loadingSessions ? '…' : sessions.length}</span>
              </div>

              {loadingSessions ? (
                <p className="account-muted">Carregando sessões...</p>
              ) : sessions.length === 0 ? (
                <p className="account-muted">Nenhuma sessão ativa no momento.</p>
              ) : (
                <ul className="account-sessions">
                  {sessions.map((session) => {
                    const parsed = parseUserAgent(session.userAgent);
                    const DeviceIcon = parsed.device === 'mobile' ? FaMobileAlt : FaDesktop;
                    return (
                      <li key={session.id} className={session.current ? 'is-current' : ''}>
                        <div className="account-session-icon" aria-hidden="true">
                          <DeviceIcon />
                        </div>
                        <div className="account-session-body">
                          <div className="account-session-title">
                            <strong>{parsed.browser} · {parsed.os}</strong>
                            {session.current && <span className="account-session-tag">Este dispositivo</span>}
                          </div>
                          <p>
                            <FaGlobe aria-hidden="true" />
                            {formatIp(session.ip)}
                            <span aria-hidden="true">·</span>
                            Último uso {formatDate(session.lastUsedAt)}
                          </p>
                          <small>Início em {formatDate(session.createdAt)}</small>
                        </div>
                        {!session.current && (
                          <button
                            type="button"
                            className="account-btn account-session-revoke"
                            onClick={() => handleRevokeSession(session.id)}
                          >
                            Encerrar
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            <section id="account-conexoes" className="account-card">
              <div className="account-card-head">
                <div>
                  <span className="account-card-eyebrow">Integrações</span>
                  <h2>Conexões</h2>
                  <p>Provedores usados para entrar na sua conta.</p>
                </div>
              </div>

              <div className="account-connection">
                <div className="account-connection-icon discord" aria-hidden="true">
                  <FaDiscord />
                </div>
                <div>
                  <strong>Discord</strong>
                  <p>
                    {hasDiscord
                      ? 'Sua conta Discord já está vinculada a este acesso.'
                      : 'Entre com Discord para sincronizar avatar e facilitar o login.'}
                  </p>
                </div>
                {hasDiscord ? (
                  <span className="account-badge ok">Conectado</span>
                ) : (
                  <button
                    type="button"
                    className="account-btn account-discord-link"
                    onClick={handleDiscordLink}
                    disabled={linkingDiscord}
                  >
                    {linkingDiscord ? 'Conectando…' : 'Conectar'}
                  </button>
                )}
              </div>
            </section>

            <section id="account-risco" className="account-card is-danger">
              <div className="account-card-head">
                <div>
                  <span className="account-card-eyebrow">Cuidado</span>
                  <h2>Zona de risco</h2>
                  <p>Ações irreversíveis ou que encerram o acesso imediatamente.</p>
                </div>
              </div>

              <div className="account-danger-list">
                <div className="account-danger-row">
                  <div>
                    <strong>Sair desta sessão</strong>
                    <p>Encerra só o dispositivo atual. Os outros continuam conectados.</p>
                  </div>
                  <button type="button" className="account-btn account-btn-ghost" onClick={() => logout()}>
                    <FaSignOutAlt /> Sair
                  </button>
                </div>

                <div className="account-danger-row">
                  <div>
                    <strong>Encerrar todas as sessões</strong>
                    <p>Desconecta todos os dispositivos, inclusive este. Você precisará entrar de novo.</p>
                  </div>
                  {confirmDanger === 'logout-all' ? (
                    <div className="account-confirm">
                      <button type="button" className="account-btn account-btn-ghost" onClick={() => setConfirmDanger(null)}>Cancelar</button>
                      <button type="button" className="account-btn account-btn-danger" onClick={() => logoutAll()}>Confirmar saída</button>
                    </div>
                  ) : (
                    <button type="button" className="account-btn account-btn-danger" onClick={() => setConfirmDanger('logout-all')}>
                      Encerrar todas
                    </button>
                  )}
                </div>

                <div className="account-danger-row">
                  <div>
                    <strong>Desativar conta</strong>
                    <p>Sua conta deixa de ficar ativa e todas as sessões são encerradas.</p>
                  </div>
                  {confirmDanger === 'deactivate' ? (
                    <div className="account-deactivate">
                      <label htmlFor="deactivateConfirm">
                        Digite <b>{user?.username}</b> para confirmar
                      </label>
                      <input
                        id="deactivateConfirm"
                        value={deactivateInput}
                        onChange={(e) => setDeactivateInput(e.target.value)}
                        placeholder={user?.username}
                        autoComplete="off"
                      />
                      <div className="account-confirm">
                        <button type="button" className="account-btn account-btn-ghost" onClick={() => { setConfirmDanger(null); setDeactivateInput(''); }}>
                          Cancelar
                        </button>
                        <button
                          type="button"
                          className="account-btn account-btn-danger"
                          disabled={deactivating || deactivateInput.trim() !== user?.username}
                          onClick={handleDeactivate}
                        >
                          {deactivating ? <FaSpinner className="account-spin" /> : null}
                          Desativar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" className="account-btn account-btn-danger" onClick={() => setConfirmDanger('deactivate')}>
                      Desativar conta
                    </button>
                  )}
                </div>
              </div>
            </section>
          </div>

          <aside className="account-community" aria-label="Comunidade Vih's">
            <section className="account-card account-suggested-card">
              <div className="account-card-head">
                <div>
                  <span className="account-card-eyebrow">Comunidade</span>
                  <h2>Conheça alguns usuários da Vih&apos;s:</h2>
                </div>
              </div>

              <div className="account-community-search">
                <FaSearch aria-hidden="true" />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value.slice(0, 40))}
                  placeholder="Buscar usuários"
                  autoComplete="off"
                />
              </div>

              {searchQuery.trim() ? (
                searching ? (
                  <div className="account-community-empty">
                    <FaSpinner className="account-spin" />
                  </div>
                ) : searchResults.length === 0 ? (
                  <p className="account-community-empty">Nenhum usuário encontrado.</p>
                ) : (
                  <ul className="account-suggested-list">
                    {searchResults.map((person) => {
                      const name = person.profile?.displayName || person.username;
                      return (
                        <li key={person.id}>
                          <Link to={`/perfil/${person.username}`} className="account-suggested-row">
                            <span className="account-suggested-avatar" aria-hidden="true">
                              {person.avatarUrl ? <img src={person.avatarUrl} alt="" /> : getInitials(name)}
                            </span>
                            <span>
                              <strong>{name}</strong>
                              <small>@{person.username}</small>
                            </span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )
              ) : loadingSuggested ? (
                <div className="account-community-empty">
                  <FaSpinner className="account-spin" />
                </div>
              ) : suggested.length === 0 ? (
                <p className="account-community-empty">Ainda não há outros usuários para sugerir.</p>
              ) : (
                <ul className="account-suggested-list">
                  {suggested.map((person) => {
                    const name = person.profile?.displayName || person.username;
                    return (
                      <li key={person.id}>
                        <Link to={`/perfil/${person.username}`} className="account-suggested-row">
                          <span className="account-suggested-avatar" aria-hidden="true">
                            {person.avatarUrl ? <img src={person.avatarUrl} alt="" /> : getInitials(name)}
                          </span>
                          <span>
                            <strong>{name}</strong>
                            <small>@{person.username}</small>
                          </span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}

              <Link to="/explorar" className="account-btn account-btn-ghost account-community-more">
                Ver mais no Explorar
              </Link>
            </section>
          </aside>
        </div>
      </div>
      <AvatarEditor
        open={avatarOpen}
        currentUrl={user?.avatarUrl}
        displayName={displayName}
        busy={avatarBusy}
        onClose={() => {
          if (!avatarBusy) setAvatarOpen(false);
        }}
        onSave={handleAvatarSave}
        onRemove={handleAvatarRemove}
      />
    </main>
  );
}
